/**
 * Frontend runtime-error reporter.
 *
 * POSTs uncaught errors and unhandled rejections to /api/client-errors,
 * where the backend logs them via structlog. Cloud Logging then forwards
 * the ERROR-severity record to GCP Error Reporting (see the processor in
 * src/api/main.py) so frontend + backend crashes land in the same
 * grouped incident view.
 *
 * Kept intentionally simple:
 *   - No queue / retry / storage — a second error that fires while the
 *     first is in flight is fine to drop; the aggregate shape on the
 *     backend is what matters.
 *   - Rate-limited client-side to one report per 2s per kind+message
 *     to stop error storms from flooding the channel.
 *   - Reports only in production-ish environments (import.meta.env.PROD)
 *     so local dev console noise doesn't get relayed.
 */

type ReportKind = 'onerror' | 'unhandledrejection' | 'boundary';

interface ClientErrorPayload {
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  col?: number;
  url?: string;
  user_agent?: string;
  app_version?: string;
  kind: ReportKind;
}

const REPORT_ENDPOINT = '/api/client-errors';
const DEDUPE_WINDOW_MS = 2000;
const _recent: Map<string, number> = new Map();

function shouldReport(kind: ReportKind, message: string): boolean {
  const key = `${kind}::${message}`;
  const now = Date.now();
  const last = _recent.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) {
    return false;
  }
  _recent.set(key, now);
  // Drop the oldest entries if the map grows unbounded (cheap pruning).
  if (_recent.size > 200) {
    const cutoff = now - DEDUPE_WINDOW_MS * 4;
    for (const [k, ts] of _recent) {
      if (ts < cutoff) _recent.delete(k);
    }
  }
  return true;
}

async function post(payload: ClientErrorPayload): Promise<void> {
  try {
    // Use keepalive so the report still goes out if the page is unloading.
    await fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'include',
    });
  } catch {
    // Silent — reporting failures must not produce more errors.
  }
}

function report(payload: ClientErrorPayload): void {
  if (!shouldReport(payload.kind, payload.message)) return;
  void post({
    ...payload,
    url: payload.url ?? window.location.href,
    user_agent: payload.user_agent ?? navigator.userAgent,
    app_version: payload.app_version ?? (import.meta.env.VITE_APP_VERSION as string | undefined),
  });
}

export function installGlobalErrorReporter(): void {
  // Only install in built/production-ish environments — dev should use
  // the browser console, not the server log.
  if (!import.meta.env.PROD) return;

  window.addEventListener('error', (e: ErrorEvent) => {
    report({
      kind: 'onerror',
      message: e.message || 'unknown error',
      stack: e.error?.stack,
      source: e.filename,
      line: e.lineno,
      col: e.colno,
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    const message =
      (reason && (reason.message ?? String(reason))) || 'unhandled promise rejection';
    report({
      kind: 'unhandledrejection',
      message,
      stack: reason?.stack,
    });
  });
}

/** Manual report path, e.g. from a React ErrorBoundary. */
export function reportBoundaryError(error: Error): void {
  report({
    kind: 'boundary',
    message: error.message,
    stack: error.stack,
  });
}
